/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { SpecificEventListener, Timeout } from "@/types";
import { getComponentConfig } from "@/utils/dom/component";
import { getElements } from "@/utils/dom/element";
import { transitionEvent } from "@/utils/dom/style";
import { FocusTrap } from "@/utils/focus-trap";
import { Component } from "./component";

export class Drawer<EventSpec extends DrawerEvents> extends Component<EventSpec> {
    static selector = 'drawer';

    static ANIMATION_OPEN_DURATION = 120;
    static ANIMATION_CLOSE_DURATION = 75;

    static defaults: Partial<DrawerOptions> = {
        bodyScrollLockClass: 'drawer-scroll-lock',
        toggleSelector: '[data-drawer-trigger="%id"]',
        initialFocusSelector: '[data-drawer-initial-focus]',
        escapeKeyCloses: true,
        clickableOverlay: true
    };

    readonly classes = {
        drawer: 'drawer',
        overlay: 'drawer__overlay',
        content: 'drawer__content',
        open: 'drawer--open',
        opening: 'drawer--opening',
        closing: 'drawer--closing'
    };

    public element: HTMLElement;
    public config!: DrawerOptions;
    public id: string;
    public isOpen: boolean = false;
    protected toggleElements!: HTMLElement[];

    protected toggleClickListener: SpecificEventListener<'click'>;
    protected keydownListener: SpecificEventListener<'keydown'>;

    protected animationTimer: Timeout = 0;
    protected animationFrame: number = 0;

    protected focusTrap: FocusTrap;

    constructor(element: HTMLElement, options: Partial<DrawerOptions>) {
        super();

        this.element = element;
        this._initConfig(options);

        this.id = this.element.dataset.drawerId || 'drawer';

        this.toggleClickListener = () => this.toggleDrawer();
        this.keydownListener = (event) => this._handleKeydown(event);

        this._initToggleElements();
        this._createContent();
        this._createOverlay();

        this.element.tabIndex = 0;
        this.element.role = 'dialog';
        this.element.dataset.role = "drawer";

        this.focusTrap = new FocusTrap(this.element, {
            initialFocusElement: this._getInitialFocusElement()
        });
    }

    /**
     * @inheritdoc
     */
    public override destroy(): void {
        super.destroy();

        this.toggleElements.forEach(element => element.removeEventListener('click', this.toggleClickListener));
        this.toggleElements = null as any;

        if (this.isOpen) {
            this.closeDrawer().then(() => {
                this.element = null as any;
            });
        } else {
            this.element = null as any;
        }
    }

    /**
     * Open drawer
     */
    public openDrawer(): Promise<void> {
        if (this.isOpen) {
            return Promise.resolve();
        }

        this.isOpen = true;
        this.trigger('opening');

        document.body.classList.add(this.config.bodyScrollLockClass);
        this.element.classList.remove(this.classes.closing);
        this.element.classList.add(this.classes.opening);
        this.element.addEventListener('keydown', this.keydownListener);
        this.element.setAttribute('aria-modal', 'true');

        return new Promise(resolve => {
            const animationEnd = () => {
                if (transitionEvent) {
                    this.element.removeEventListener(transitionEvent, animationEnd);
                }
                this._handleAnimationTimerEnd();
                this.focusTrap.trapFocus();
                this._onOpen();
                this.trigger('opened');
                resolve();
            };

            this._runNextAnimationFrame(() => {
                this.element.classList.add(this.classes.open);

                if (transitionEvent) {
                    this.element.addEventListener(transitionEvent, animationEnd);
                } else {
                    this.animationTimer = setTimeout(animationEnd, Drawer.ANIMATION_OPEN_DURATION);
                }
            });
        });
    }

    /**
     * Close drawer
     */
    public closeDrawer(): Promise<void> {
        if (!this.isOpen) {
            return Promise.resolve();
        }

        this.element.setAttribute('aria-modal', 'false');
        this.element.removeEventListener('keydown', this.keydownListener);
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = 0;
        this.isOpen = false;
        this.trigger('closing');

        this.element.classList.add(this.classes.closing);
        this.element.classList.remove(this.classes.opening);
        document.body.classList.remove(this.config.bodyScrollLockClass);

        return new Promise(resolve => {
            const animationEnd = () => {
                if (transitionEvent) {
                    this.element.removeEventListener(transitionEvent, animationEnd);
                }
                this.focusTrap.releaseFocus();
                this._handleAnimationTimerEnd();
                this._onClose();
                this.trigger('closed');
                resolve();
            };

            this._runNextAnimationFrame(() => {
                this.element.classList.remove(this.classes.open);

                if (transitionEvent) {
                    this.element.addEventListener(transitionEvent, animationEnd);
                } else {
                    this.animationTimer = setTimeout(animationEnd, Drawer.ANIMATION_CLOSE_DURATION);
                }
            });
        });
    }

    /**
     * Toggle drawer opened state
     */
    public toggleDrawer() {
        return this.isOpen ? this.closeDrawer() : this.openDrawer();
    }

    /**
     * Initialize configuration
     */
    protected _initConfig(options: Partial<DrawerOptions>) {
        this.config = {
            ...Drawer.defaults,
            ...getComponentConfig(Drawer.selector, this.element),
            ...options
        };
    }

    /**
     * Initialize drawer toggle elements
     */
    protected _initToggleElements() {
        this.toggleElements = getElements(this.config.toggleSelector.replace('%id', this.id));
        this.toggleElements.forEach(element => element.addEventListener('click', this.toggleClickListener));
    }

    protected _onOpen() {}

    protected _onClose() {}

    protected _handleKeydown(event: KeyboardEvent) {
        if (event.key.toLowerCase() === 'escape') {
            this.config.escapeKeyCloses && this.closeDrawer();
        }
    }

    protected _runNextAnimationFrame(callback: () => void) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = requestAnimationFrame(() => {
            this.animationFrame = 0;
            clearTimeout(this.animationTimer);
            this.animationTimer = setTimeout(callback);
        });
    }

    protected _handleAnimationTimerEnd() {
        this.animationTimer = 0;
        this.element.classList.remove(this.classes.opening, this.classes.closing);
    }

    protected _getInitialFocusElement(): HTMLElement | null {
        return this.config.initialFocusSelector
            && this.element.querySelector<HTMLElement>(this.config.initialFocusSelector)
            || null;
    }

    /**
     * Create content element if not exists
     */
    protected _createContent() {
        let content = this.element.querySelector<HTMLElement>(this.classes.content);

        if (!content) {
            content = document.createElement('div');
            content.classList.add(this.classes.content);
            content.append(...this.element.children);
            this.element.append(content);
        }
    }

    /**
     * Create overlay element if not exists
     */
    protected _createOverlay() {
        let overlay = this.element.querySelector<HTMLElement>(this.classes.overlay);

        if (!overlay) {
            overlay = document.createElement('div');
            overlay.classList.add(this.classes.overlay);
            this.element.prepend(overlay);
        }

        if (!this.toggleElements.includes(overlay)) {
            overlay.addEventListener('click', this.toggleClickListener);
            this.toggleElements.push(overlay);
        }
    }
}

export interface DrawerEvents {
    opening: never
    opened: never
    closing: never
    closed: never
}

export interface DrawerOptions {
    bodyScrollLockClass: string
    toggleSelector: string
    escapeKeyCloses: boolean
    clickableOverlay: boolean,
    initialFocusSelector: string
}
